import axios from 'axios';
import { useSessionStore } from '@stores/session';
import { Capacitor } from '@capacitor/core';

let messaging = null;
let firebaseInitialized = false;

// Pas 1.1: firebase only ever initializes on native (non-iOS) builds
// with a server-provided firebaseConfig — web visitors must not
// download it in the initial bundle. Dynamic import keeps the exact
// same init point (awaited inside _loadConfigInternal).
async function initFirebaseMessaging(firebaseConfig: any): Promise<void> {
    if (firebaseInitialized || !__IS_NATIVE__ || __PLATFORM__ === 'ios') return;
    if (!firebaseConfig?.apiKey) return;
    const [{ initializeApp }, { getMessaging }] = await Promise.all([
        import('firebase/app'),
        import('firebase/messaging'),
    ]);
    initializeApp(firebaseConfig);
    messaging = getMessaging() as any;
    firebaseInitialized = true;
}

class ConfigService {
    static lastCustomerId = '';
    static pendingPromise:Promise<any> | null = null;
    static lastParams: { customerId: string | null, shortUrl: string | null } = { customerId: null, shortUrl: null };
    
    static async loadConfig(customerId: string | null, shortUrl: string | null): Promise<any> {
        // Normalize: callers pass null (provider/AutoService) or undefined
        // (router `to.query?.customerId`) for "no customer". Without this the
        // dedup below misses, two concurrent fetches race, and the loser
        // resolves a null store config that breaks Amplify init at boot.
        customerId = customerId ?? null;
        shortUrl = shortUrl ?? null;
        // Check if we have an existing promise with the same params
        if (this.pendingPromise &&
            this.lastParams.customerId === customerId &&
            this.lastParams.shortUrl === shortUrl) {
            return this.pendingPromise;
        }
        
        // Store the parameters for comparison
        this.lastParams = { customerId, shortUrl };
        this.pendingPromise = this._loadConfigInternal(customerId, shortUrl);
        
        return this.pendingPromise;
    }
    
    static async _loadConfigInternal(customerId: string | null, shortUrl: string | null): Promise<any> {
        const localeStore = useSessionStore();

        if (shortUrl == null || shortUrl == undefined)
        {
            if (localeStore.appConfig?.hostname === (__IS_NATIVE__ ? __APP_HOSTNAME__ : window.location.host))
            {
               return localeStore.appConfig;
            }
            // Hostname differs from the loaded config (e.g. www. alias,
            // cloudfront.net domain) — refetch below. Do NOT wipe appConfig
            // here: the gap between clear and refetch made every API call
            // crash on null.domain.
            if (localeStore.appConfig !== null && localeStore.appConfig !== undefined) {
                await initFirebaseMessaging(localeStore.appConfig.firebaseConfig);
               
                if (customerId == null || customerId == undefined) {
                    if (localeStore.appConfig.defaultcustomerId) {
                        return localeStore.appConfig;
                    }
                }

                if (customerId && localeStore.appConfig?.defaultcustomerId && localeStore.appConfig?.defaultcustomerId == customerId) {
                    return localeStore.appConfig;  
                }
            }
        }
        
        try {
            const baseURL = (import.meta.env.VITE_AMAZON_API || '').trim();
            const params = new URLSearchParams({ hostname: __IS_NATIVE__ ? __APP_HOSTNAME__ : window.location.host });
            if (customerId) params.set('customerId', customerId);
            if (shortUrl) params.set('shortUrl', shortUrl);
            const url = `${baseURL}/api/config?${params.toString()}`;
            // Bounded wait: a hung /api/config would otherwise freeze the
            // router guard on a blank page forever.
            const response = await axios.get(url, { timeout: 15000 });
            // Stale-response guard: a newer loadConfig with different params
            // supersedes this one — never let an older reply clobber the
            // newer config.
            if (this.lastParams.customerId !== customerId || this.lastParams.shortUrl !== shortUrl) {
                return localeStore.appConfig;
            }
            if (response.data)
            {
                localeStore.setAppConfig(response.data);
                if (response.data.defaultcustomerId) {
                    localeStore.setCustomerId(response.data.defaultcustomerId);
                }
                await initFirebaseMessaging(response.data.firebaseConfig);
            }
        } catch (error) {
            // Clear the pending promise on error
            this.pendingPromise = null;
            throw error;
        }
        return localeStore.appConfig; 
    }
}

export { ConfigService, messaging  };